import React, { useState, useRef, useEffect, useMemo } from "react"
import { Message } from 'primereact/message'
import { Card } from "primereact/card";
import { Button } from "primereact/button"; // Added Button import
import { Dialog } from "primereact/dialog"; // Added Dialog import
import { useNavigate } from "react-router-dom";
import { useCarCalculator } from "../../../../Hooks/useCarCalculator";
import { useBudgetPDF } from "../../../../Hooks/useBudgetPDF";
import { CarDataForm } from "../../../Cotizador/Steps/Step1/CarDataForm";
import { downloadPDF } from "../../../Cotizador/functions/downloadPDF";
import { AppButton, AppNumberField } from "../../../../Components/FormKit";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeflex/primeflex.css";
import DialogTermsFinance from "./DialogTermsFinance";
import DialogCalculatorAdmin from "./DialogCalculatorAdmin";
import InfoFinance from "./InfoFinance";
import { QuoteStepperPanel } from "./PreLoanSteps/StepperPanel";
import { TbDeviceUsb } from "react-icons/tb";
import { dashboardStyles } from "../DashboardWrapper.css";
import { css } from '@emotion/react';

const fmtMoney = (n) => new Intl.NumberFormat('es-MX', {
    style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(Number(n) || 0);

const PCT_HELP = 'Captura el porcentaje como número. Ejemplo: 25 = 25 % (no escribas 0.25). Déjalo vacío para usar la tasa global.';

// Construye el patch optimista de financial_details a partir del override aplicado.
// extra = { manual_overrides: {tasa,commission,gps} } | { clear_override: true }
function computeOverridePatch(infoBudget, extra) {
    const fd = infoBudget?.financial_details || {};
    const isLegacy = fd.typeBudget === 'finance';
    const globalTasa = parseFloat(infoBudget?.tasa);
    const globalComm = parseFloat(infoBudget?.commission);
    const globalGps = parseFloat(infoBudget?.gps);
    const clear = !!extra?.clear_override;
    const entered = extra?.manual_overrides || {};
    // manual_overrides es AUTORITATIVO: un campo null/vacío = "usar global"
    // (NO se mezcla con el override previo, para que borrar un campo vuelva al global).
    const newMO = clear
        ? { tasa: null, commission: null, gps: null }
        : {
            tasa: entered.tasa ?? null,
            commission: entered.commission ?? null,
            gps: entered.gps ?? null,
        };
    const effTasaPct = newMO.tasa ?? globalTasa;
    const effCommPct = newMO.commission ?? globalComm;
    const effGps = newMO.gps ?? globalGps;
    return {
        manual_override: newMO,
        tasa: isLegacy ? effTasaPct / 100 : effTasaPct,
        commission: isLegacy ? effCommPct / 100 : effCommPct,
        gps: effGps,
        typeBudget: fd.typeBudget,
    };
}

// Header de card al estilo del módulo de seguros (tk-table-card).
const cardHeader = (title, icon) => (
    <div style={{
        background: 'var(--pei-bg-soft, #f8fafc)',
        padding: '0.65rem 1rem',
        borderBottom: '1px solid var(--pei-line, #e2e8f0)',
        borderRadius: '14px 14px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    }}>
        {icon && <i className={icon} style={{ color: 'var(--pei-blue-deep, #21588C)', fontSize: '0.85rem' }} />}
        <span style={{ fontWeight: 700, fontSize: '0.72rem', color: 'var(--pei-blue-deep, #21588C)', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
            {title}
        </span>
        <div style={{ flex: 1, height: '1px', background: '#dbeafe' }} />
    </div>
);

// Tile de valor (solo lectura) para los parámetros de la cotización.
// `pending`: si hay una tasa manual pendiente de aplicar, el valor que se enviará al cotizar.
const ParamTile = ({ label, value, accent, manual, pending }) => (
    <div className="col-12 md:col-4">
        <div style={{ border: pending ? '1px solid rgba(255,108,14,0.45)' : '1px solid var(--pei-line, #e2e8f0)', borderRadius: '12px', padding: '0.85rem 1rem', background: '#fff', height: '100%' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--pei-text-helper, #64748b)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {label}
            </div>
            <div style={{ fontSize: '1.55rem', fontWeight: 700, color: accent, marginTop: '0.35rem', lineHeight: 1.1, textDecoration: pending ? 'line-through' : 'none', opacity: pending ? 0.55 : 1 }}>
                {value}
            </div>
            {pending && (
                <div style={{ marginTop: '0.4rem', display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '0.72rem', fontWeight: 700, color: 'var(--pei-orange, #FF6C0E)', background: '#FFF7ED', border: '1px solid rgba(255,108,14,0.30)', borderRadius: '8px', padding: '0.15rem 0.5rem' }}>
                    <i className="pi pi-arrow-right" style={{ fontSize: '0.6rem' }} /> Al cotizar: {pending}
                </div>
            )}
            {!pending && manual && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '0.4rem', fontSize: '0.7rem', fontWeight: 600, color: '#0369a1' }}>
                    <i className="pi pi-pencil" style={{ fontSize: '0.62rem' }} /> valor manual
                </span>
            )}
        </div>
    </div>
);

function NewCalculator({ budgetData, id, isAdmin = false, onSuccess, persona, toast, setLoading }) {
    const [step, setStep] = useState(1);
    const navigate = useNavigate();

    // Estado del override de tasa SOLO para esta cotización
    const [showManualOverride, setShowManualOverride] = useState(false);
    const [ovTasa, setOvTasa] = useState(null);
    const [ovCommission, setOvCommission] = useState(null);
    const [ovGps, setOvGps] = useState(null);
    const [pendingOverride, setPendingOverride] = useState(null); // override recordado para aplicar al pulsar "Cotizar"

    const internalVariables = useRef({
        secret: null, // Initialize with null, will be updated by useEffect
        budget_id: null, // Initialize with null, will be updated by useEffect
    });

    // Update internalVariables ref whenever budgetData changes
    useEffect(() => {
        internalVariables.current.secret = budgetData?.secret;
        internalVariables.current.budget_id = budgetData?.budget_id;
    }, [budgetData]);

    const {
        calculatorState,
        setState,
        loading,
        plazoVal,
        setPlazoVal,
        plazoTimer,
        submit: originalSubmit,
        loadInsurances,
        cleanErrors,
        setClass,
        sumAmountAndAccesories,
        typeRegime,
        residualValue,
        stateAmple,
        percentageDownPayment,
        hadleResidualValue,
        type_coverage,
        handleStatePlateChange,
    } = useCarCalculator(internalVariables, setStep, budgetData, id, isAdmin, budgetData?.secret, budgetData?.budget_id);

    // Función para descargar el PDF de la cotización
    const handleDownloadPDF = () => {
        if (!calculatorState.resumen || !calculatorState.detalle) {
            toast.current.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'No hay datos de cotización disponibles para descargar',
                life: 3000
            });
            return;
        }

        // Preparar allDetalleState desde pdfPlazo si existe
        const allDetalleState = calculatorState.pdfPlazo || [];

        downloadPDF(calculatorState.resumen, calculatorState.detalle, allDetalleState);
    };

    // Wrapper del submit para manejar la descarga automática del PDF en admin
    const handleSubmit = async () => {
        try {
            // Si hay una tasa manual pendiente, se aplica en esta cotización.
            const wasPending = pendingOverride;
            const result = await originalSubmit(wasPending || {});

            if (result && isAdmin) {
                // Descargar PDF automáticamente después de cotizar
                if (result.resumen && result.detalle) {
                    downloadPDF(result.resumen, result.detalle, result.pdfPlazo || []);
                }
                if (wasPending) {
                    setPendingOverride(null);
                    toast.current.show({
                        severity: 'success',
                        summary: wasPending.clear_override ? 'Tasa global restaurada' : 'Tasa manual aplicada',
                        detail: 'La cotización se recalculó con la tasa indicada.',
                        life: 4000,
                    });
                }
                // Actualización COMPLETA del infoBudget (incluye financial_details con los montos
                // recalculados + el manual_override aplicado): el backend devuelve result.data.infoBudget.
                if (onSuccess) onSuccess(result);
            }
        } catch (error) {
            console.error('Error al cotizar:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al procesar la cotización. Por favor intenta de nuevo.',
                life: 5000
            });
        }
    };

    // Verificar si hay datos disponibles para descargar
    const canDownloadPDF = calculatorState?.resumen && calculatorState?.detalle;

    // ---------- Parámetros de ESTA cotización (solo lectura) ----------
    const fd = persona?.infoBudget?.financial_details || {};
    const mo = fd.manual_override || {};
    const isLegacyBudget = fd.typeBudget === 'finance'; // legacy guarda tasa/comisión en decimal
    const isExcelBudget = fd.typeBudget === 'excel_cotizador3'; // calc 3: no aplica override
    const toPct = (v) => (isLegacyBudget ? parseFloat(v) * 100 : parseFloat(v));
    const tasaShown = mo.tasa != null ? mo.tasa : (fd.tasa != null ? toPct(fd.tasa) : null);
    const commShown = mo.commission != null ? mo.commission : (fd.commission != null ? toPct(fd.commission) : null);
    const gpsShown = mo.gps != null ? mo.gps : (fd.gps != null ? parseFloat(fd.gps) : null);
    const hasManual = mo.tasa != null || mo.commission != null || mo.gps != null;
    const tasaGlobal = parseFloat(persona?.infoBudget?.tasa || 0);
    const commGlobal = parseFloat(persona?.infoBudget?.commission || 0);
    const gpsGlobal = parseFloat(persona?.infoBudget?.gps || 0);

    // Valor que se ENVIARÁ al cotizar si hay una tasa manual pendiente (mismo cálculo que se aplicará).
    const pendingPatch = pendingOverride ? computeOverridePatch(persona?.infoBudget, pendingOverride) : null;
    const pendTasaPct = pendingPatch ? (isLegacyBudget ? pendingPatch.tasa * 100 : pendingPatch.tasa) : null;
    const pendCommPct = pendingPatch ? (isLegacyBudget ? pendingPatch.commission * 100 : pendingPatch.commission) : null;
    const pendGpsVal = pendingPatch ? pendingPatch.gps : null;
    const _differs = (a, b) => a != null && (b == null || Math.abs(a - b) > 0.001);
    const pendingTasaStr = pendingPatch && _differs(pendTasaPct, tasaShown) ? `${pendTasaPct.toFixed(2)}%` : null;
    const pendingCommStr = pendingPatch && _differs(pendCommPct, commShown) ? `${pendCommPct.toFixed(2)}%` : null;
    const pendingGpsStr = pendingPatch && _differs(pendGpsVal, gpsShown) ? fmtMoney(pendGpsVal) : null;

    const openManualOverride = () => {
        // Pre-llenar la tasa con el valor actual de ESTA cotización (no vacío).
        setOvTasa(mo.tasa != null ? mo.tasa : (tasaShown != null ? Number(tasaShown.toFixed(2)) : null));
        // Comisión y GPS solo se pre-llenan si ya son manuales; vacío = usar global.
        setOvCommission(mo.commission != null ? mo.commission : null);
        setOvGps(mo.gps != null ? mo.gps : null);
        setShowManualOverride(true);
    };

    // Guarda la tasa manual como PENDIENTE (no recotiza aquí) y cierra el modal.
    // Se aplica cuando el admin pulsa "COTIZAR" en la calculadora (handleSubmit).
    const recotizarConOverride = ({ clear = false } = {}) => {
        let extra;
        if (clear) {
            extra = { clear_override: true };
        } else {
            const entered = {
                tasa: ovTasa === null || ovTasa === '' ? null : Number(ovTasa),
                commission: ovCommission === null || ovCommission === '' ? null : Number(ovCommission),
                gps: ovGps === null || ovGps === '' ? null : Number(ovGps),
            };
            extra = { manual_overrides: entered };
        }
        setPendingOverride(extra);
        setShowManualOverride(false);
        toast.current.show({
            severity: 'info',
            summary: clear ? 'Se quitará la tasa manual' : 'Tasa lista para aplicar',
            detail: 'Pulsa “COTIZAR” en la calculadora para recalcular con este valor.',
            life: 5000,
        });
    };

    return (
        <div className="col-12">
            <p className="text-sm  mt-2" style={{ fontStyle: 'italic' }}>
                Nota: Al modificar la cotización, se actualizarán datos clave como la cobertura de seguro, el plazo de financiamiento y otras condiciones importantes.
            </p>

            {persona.infoBudget.financial_details && (
                <div className="col-12 mb-4">
                    <div className="tk-table-card">
                        {cardHeader('Parámetros de esta cotización', 'pi pi-calculator')}
                        <div style={{ padding: '1rem' }} className="flex flex-column gap-3">

                            {hasManual && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#E3F2FF', border: '1px solid rgba(50,184,223,0.35)', borderRadius: '10px', padding: '0.5rem 0.85rem' }}>
                                    <i className="pi pi-user-edit" style={{ color: '#0369a1', fontSize: '0.85rem' }} />
                                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#0369a1' }}>
                                        Tasa manual aplicada solo a esta cotización
                                    </span>
                                </div>
                            )}

                            <div className="grid">
                                <ParamTile
                                    label={`Tasa de interés · ${persona.infoBudget.vehicule_type}`}
                                    value={tasaShown != null ? tasaShown.toFixed(2) + '%' : 'N/A'}
                                    accent="var(--pei-cyan, #32B8DF)"
                                    manual={mo.tasa != null}
                                    pending={pendingTasaStr}
                                />
                                <ParamTile
                                    label="Comisión por apertura"
                                    value={commShown != null ? commShown.toFixed(2) + '%' : 'N/A'}
                                    accent="var(--pei-green-true, #00BA6E)"
                                    manual={mo.commission != null}
                                    pending={pendingCommStr}
                                />
                                <ParamTile
                                    label="Costo de GPS"
                                    value={gpsShown != null ? fmtMoney(gpsShown) : 'N/A'}
                                    accent="var(--pei-orange, #FF6C0E)"
                                    manual={mo.gps != null}
                                    pending={pendingGpsStr}
                                />
                            </div>

                            <div className="flex gap-2 flex-wrap justify-content-end">
                                {!isExcelBudget && (
                                    <AppButton
                                        label="Cambiar tasa de esta cotización"
                                        icon="pi pi-pencil"
                                        variant="info"
                                        size="sm"
                                        onClick={openManualOverride}
                                    />
                                )}
                                <AppButton
                                    label="Editar tasas globales"
                                    icon="pi pi-cog"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate('/admin/tasas-financieras')}
                                />
                            </div>

                            <div style={{ background: '#ECF8FE', border: '1px solid rgba(50,184,223,0.30)', borderRadius: '10px', padding: '0.7rem 0.9rem' }} className="flex align-items-start gap-2">
                                <i className="pi pi-info-circle" style={{ color: 'var(--pei-cyan, #32B8DF)', marginTop: '2px' }} />
                                <span style={{ fontSize: '0.8rem', color: '#075985' }}>
                                    Estos son los valores con los que se calculó <strong>esta</strong> cotización.
                                    {isExcelBudget
                                        ? ' Esta cotización usa la Calculadora 3 (Excel), que maneja sus propios valores; la tasa manual no aplica aquí.'
                                        : ' Para cambiar las tasas de todo el sistema usa “Editar tasas globales”.'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Botón para descargar PDF si hay datos disponibles */}
            {canDownloadPDF && (
                <div className="mb-3">
                    <Button
                        label="Descargar PDF de Cotización"
                        icon="pi pi-file-pdf"
                        className="p-button-outlined p-button-info"
                        onClick={handleDownloadPDF}
                    />
                </div>
            )}

            {pendingOverride && (
                <div className="mb-3 flex align-items-center justify-content-between gap-2 flex-wrap"
                    style={{ background: '#FFF7ED', border: '1px solid rgba(255,108,14,0.30)', borderRadius: '10px', padding: '0.6rem 0.9rem' }}>
                    <span style={{ fontSize: '0.82rem', color: '#92400E', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i className="pi pi-clock" style={{ color: 'var(--pei-orange, #FF6C0E)' }} />
                        {pendingOverride.clear_override
                            ? 'Se quitará la tasa manual al cotizar.'
                            : `Tasa manual pendiente${pendingOverride.manual_overrides?.tasa != null ? ` (${pendingOverride.manual_overrides.tasa}%)` : ''}. Pulsa “Cotizar” para aplicarla.`}
                    </span>
                    <AppButton label="Cancelar" icon="pi pi-times" variant="ghost" size="sm" onClick={() => setPendingOverride(null)} />
                </div>
            )}

            <CarDataForm
                state={calculatorState}
                setState={setState}
                loading={loading}
                plazoVal={plazoVal}
                setPlazoVal={setPlazoVal}
                plazoTimer={plazoTimer}
                submit={handleSubmit}
                loadInsurances={loadInsurances}
                cleanErrors={cleanErrors}
                setClass={setClass}
                sumAmountAndAccesories={sumAmountAndAccesories}
                typeRegime={typeRegime}
                residualValue={residualValue}
                stateAmple={stateAmple}
                percentageDownPayment={percentageDownPayment}
                hadleResidualValue={hadleResidualValue}
                type_coverage={type_coverage}
                handleStatePlateChange={handleStatePlateChange}
                isAdmin={isAdmin}
                hasPendingOverride={!!pendingOverride}
            />

            {/* Modal: cambiar la tasa SOLO de esta cotización */}
            <Dialog
                visible={showManualOverride}
                onHide={() => setShowManualOverride(false)}
                style={{ width: '520px', maxWidth: '95vw' }}
                breakpoints={{ '641px': '95vw' }}
                showHeader={false}
                contentStyle={{ padding: 0, borderRadius: 16, overflow: 'hidden' }}
                dismissableMask
                modal
                draggable={false}
            >
                <div className="fk-root" style={{ fontFamily: 'var(--pei-font)' }}>
                    {/* HEADER */}
                    <div style={{ padding: '22px 26px 14px', borderBottom: '1px solid var(--pei-line, #e2e8f0)', background: 'var(--pei-bg-soft, #f8fafc)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0, background: 'rgba(50,184,223,0.14)', color: 'var(--pei-cyan, #32B8DF)', display: 'grid', placeItems: 'center' }}>
                                <i className="pi pi-percentage" style={{ fontSize: 18 }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.10em', color: 'var(--pei-text-helper, #64748b)' }}>
                                    Tasa de esta cotización
                                </div>
                                <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--pei-text, #0F172A)', lineHeight: 1.2 }}>
                                    Cambiar tasa de esta cotización
                                    {persona?.infoBudget?.budget_id ? (
                                        <span style={{ color: 'var(--pei-cyan, #32B8DF)' }}> · #{persona.infoBudget.budget_id}</span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* BODY */}
                    <div style={{ padding: '18px 26px 6px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div style={{
                            padding: '12px 14px', borderRadius: 12, background: 'var(--pei-bg-soft, #F8FAFC)',
                            border: '1px solid var(--pei-line, #e2e8f0)', fontSize: 13, color: 'var(--pei-text-label, #475569)',
                            display: 'flex', flexDirection: 'column', gap: 8,
                        }}>
                            <span>
                                Vehículo <strong style={{ color: 'var(--pei-text, #0F172A)' }}>{persona?.infoBudget?.vehicule_type}</strong>
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 11.5, color: 'var(--pei-text-helper, #64748b)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>
                                    Valores globales:
                                </span>
                                <span>Tasa <strong style={{ color: 'var(--pei-cyan, #32B8DF)' }}>{tasaGlobal.toFixed(2)}%</strong></span>
                                <span>Comisión <strong style={{ color: 'var(--pei-green-true, #00BA6E)' }}>{commGlobal.toFixed(2)}%</strong></span>
                                <span>GPS <strong style={{ color: 'var(--pei-orange, #FF6C0E)' }}>{fmtMoney(gpsGlobal)}</strong></span>
                            </div>
                        </div>

                        <AppNumberField
                            label="Tasa de interés para esta cotización"
                            value={ovTasa}
                            onValueChange={(e) => setOvTasa(e.value)}
                            mode="decimal"
                            suffix="%"
                            min={0}
                            max={100}
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            placeholder={`Vacío = global (${tasaGlobal.toFixed(2)} %)`}
                            helperText={PCT_HELP}
                        />

                        <AppNumberField
                            label="Comisión por apertura (opcional)"
                            value={ovCommission}
                            onValueChange={(e) => setOvCommission(e.value)}
                            mode="decimal"
                            suffix="%"
                            min={0}
                            max={100}
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            placeholder={`Vacío = global (${commGlobal.toFixed(2)} %)`}
                            helperText="Mismo formato: 4.6 = 4.6 %. Vacío = usar la comisión global."
                        />

                        <AppNumberField
                            label="Costo de GPS (opcional)"
                            value={ovGps}
                            onValueChange={(e) => setOvGps(e.value)}
                            mode="currency"
                            currency="MXN"
                            locale="es-MX"
                            min={0}
                            placeholder={`Vacío = global (${fmtMoney(gpsGlobal)})`}
                            helperText="Monto en pesos. Vacío = usar el costo de GPS global."
                        />

                        <div style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10,
                            padding: '12px 14px', borderRadius: 12,
                            background: '#FFF7ED', border: '1px solid rgba(255,108,14,0.30)',
                        }}>
                            <i className="pi pi-exclamation-triangle" style={{ color: 'var(--pei-orange, #FF6C0E)', marginTop: 2, fontSize: 14 }} />
                            <span style={{ fontSize: 12.5, color: '#92400E', lineHeight: 1.45 }}>
                                Esto <strong>NO</strong> cambia la tasa global del sistema. Solo afecta a esta cotización.
                                Al guardar, pulsa <strong>COTIZAR</strong> en la calculadora para recalcular.
                            </span>
                        </div>
                    </div>

                    {/* FOOTER */}
                    <div style={{ padding: '14px 26px 20px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <AppButton
                            label="Volver a tasa global"
                            icon="pi pi-undo"
                            variant="ghost"
                            size="sm"
                            onClick={() => recotizarConOverride({ clear: true })}
                            disabled={!hasManual}
                        />
                        <div style={{ flex: 1 }} />
                        <AppButton
                            label="Cancelar"
                            icon="pi pi-times"
                            variant="secondary"
                            size="sm"
                            onClick={() => setShowManualOverride(false)}
                        />
                        <AppButton
                            label="Guardar"
                            icon="pi pi-check"
                            variant="primary"
                            size="sm"
                            onClick={() => recotizarConOverride({})}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}

export default function CardsWelcome({ persona, setSelectedPerson, setSelectedPersonDirect, toast, setLoading }) {
    const [showCalculatorModal, setShowCalculatorModal] = useState(false); // New state for modal
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    // Use custom hook for PDF operations
    const { generateAndDownloadPDF, loading: loadingPDF } = useBudgetPDF(persona, toast);

    // NewCalculator cotiza con la ruta -v2, que espera el TOTAL del pago inicial
    // (enganche + apertura + originación, con IVA). infoBudget.down_payment es el
    // enganche INTERNO, así que se usa pago_inicial_total cuando el backend lo manda.
    // (RecotizacionDialog usa la ruta sin -v2 y sí necesita el enganche interno.)
    const infoBudget = persona?.infoBudget;
    const calculatorBudgetData = useMemo(() => {
        if (!infoBudget) return infoBudget;
        return {
            ...infoBudget,
            down_payment: infoBudget.pago_inicial_total ?? infoBudget.down_payment,
        };
    }, [infoBudget]);

    // Callback después de cotizar exitosamente
    const handleCotizacionSuccess = (result) => {
        // Si es actualización financiera, usar setter directo para evitar el reset completo de onChangePersonFetch
        if (result?.isFinancialUpdate) {
            if (setSelectedPersonDirect) {
                setSelectedPersonDirect(prev => {
                    const updatedInfoBudget = { ...prev.infoBudget };
                    if (result.data?.infoBudget) {
                        updatedInfoBudget.tasa = result.data.infoBudget.tasa ?? prev.infoBudget.tasa;
                        updatedInfoBudget.commission = result.data.infoBudget.commission ?? prev.infoBudget.commission;
                        updatedInfoBudget.gps = result.data.infoBudget.gps ?? prev.infoBudget.gps;
                    }
                    return { ...prev, infoBudget: updatedInfoBudget };
                });
            }
            return;
        }

        // Si hay datos del usuario actualizado en la respuesta, actualizar selectedPerson
        if (result && result.data && setSelectedPerson) {
            setSelectedPerson({
                ...persona,
                infoBudget: result.data.infoBudget
            });
        }

        // Cerrar modal después de un pequeño delay para que el usuario vea el mensaje de éxito
        setTimeout(() => {
            setShowCalculatorModal(false);
        }, 2000);
    };

    let thereIsSections = persona?.sections.length > 0;


    return (
        <div className="grid mt-3" css={dashboardStyles()} >

            {/* Alerta cuando no hay documentos */}
            {!thereIsSections && (
                <div className="col-12">
                    <div className="p-message p-component p-message-error w-full">
                        <div className="p-message-wrapper">
                            <span className="p-message-icon pi pi-times-circle mr-2" style={{ fontSize: '1rem' }}></span>
                            <span className="p-message-text">
                                El usuario {persona?.name || 'seleccionado'} no tiene documentos disponibles
                            </span>
                            {!persona?.infoBudget && (
                                <> <br /> <span className="p-message-text">No hay información del Cliente</span></>
                            )}
                        </div>
                    </div>
                </div>
            )}



            <div className="col-12">
                <div className="p-4 border-round-lg border-1 border-gray-200 shadow-sm">
                    <div className="flex align-items-center gap-3 mb-3">
                        <i className="pi pi-home text-blue-600 text-2xl"></i>
                        <h3 className="m-0 text-gray-800">¡Bienvenido al Panel de Documentos!</h3>
                    </div>
                    <p className="text-gray-600 m-0 mb-3">
                        Aquí puedes gestionar y revisar todos los documentos de <strong>{persona?.name}</strong>.

                    </p>
                </div>
            </div>



            {/* Información del Presupuesto */}
            {persona?.infoBudget ? (
                <InfoFinance
                    persona={persona}
                    showDetailsModal={showDetailsModal}
                    setShowDetailsModal={setShowDetailsModal}
                    setShowCalculatorModal={setShowCalculatorModal}
                    handleDownloadExistingPDF={generateAndDownloadPDF}
                    loadingPDF={loadingPDF}
                />
            ) : (
                <div className="col-12">
                    <div className="p-4 border-round-lg border-1 border-gray-200 shadow-sm h-full">
                        <div className="flex align-items-center gap-3 mb-3">
                            <i className="pi pi-info-circle text-blue-600 text-xl"></i>
                            <h4 className="m-0 text-gray-800">No hay información del Cliente</h4>
                        </div>
                    </div>
                </div>
            )}

            <DialogCalculatorAdmin
                showCalculatorModal={showCalculatorModal}
                setShowCalculatorModal={setShowCalculatorModal}
                persona={persona}
                handleCotizacionSuccess={handleCotizacionSuccess}
                handleDownloadExistingPDF={generateAndDownloadPDF}
                loadingPDF={loadingPDF}
            >
                <NewCalculator
                    budgetData={calculatorBudgetData}
                    id={persona?.id}
                    isAdmin={true}
                    onSuccess={handleCotizacionSuccess}
                    persona={persona}
                    toast={toast}
                    setLoading={setLoading}
                />
            </DialogCalculatorAdmin>

            {/* Modal para desglose completo */}
            <DialogTermsFinance
                showDetailsModal={showDetailsModal}
                setShowDetailsModal={setShowDetailsModal}
                persona={persona}
            />
        </div>
    )
}
